import { subtract } from '@flatten-js/boolean-op';
import { Point, Polygon, Segment, Vector } from '@flatten-js/core';
import { createPolygonsFromIntersections } from './poly-split-breakerboard';

// returns a new segment which has been prolonged by the given amount, keeping the center and slope of the original segment
function prolongSegment(segment, extraLength) {
  const extraVector = new Vector(segment.ps, segment.pe).normalize().multiply(extraLength / 2);
  const startVector = new Vector(segment.ps.x, segment.ps.y).subtract(extraVector);
  const endVector = new Vector(segment.pe.x, segment.pe.y).add(extraVector);
  return new Segment(new Point(startVector.x, startVector.y), new Point(endVector.x, endVector.y));
}

export function rectangleFromSide(segment, width) {
  const displacementVector = new Vector(segment.ps, segment.pe)
    .rotate90CW()
    .normalize()
    .multiply(width);
  const startVector = new Vector(segment.ps.x, segment.ps.y);
  const endVector = new Vector(segment.pe.x, segment.pe.y);
  const parallelSegment = [endVector.add(displacementVector), startVector.add(displacementVector)];

  const vertices = [
    segment.ps,
    segment.pe,
    new Point(parallelSegment[0].x, parallelSegment[0].y),
    new Point(parallelSegment[1].x, parallelSegment[1].y),
  ];

  const polygon = new Polygon();
  polygon.addFace(vertices);
  return polygon;
}

/**
 * Splits a polygon using a segment
 * IMPORTANT: cutSegment must intersect with the polygon contour or, in other words,
 * cutSegment start and end points must be on the contour or outside the polygon
 *
 * @param {Flatten.Segment} cutSegment - Use this segment to split polygon
 * @param {Flatten.Polygon} polygon - Polygon to split
 * @param {number} [precision=0.001] - Points which difference is below this value are considered equal
 * @returns {Flatten.Polygon[]} - The polygons resulting of the provided cut
 */
function splitPolygon(cutSegment, polygon, precision = 0.01) {
  // subtract function returns the result of removing a polygon from another one. Therefore:
  // 1 - create small polygon for the cut. cutSegment must be bigger than polygon to cut (needs to be prolonged)
  const polyMainOrientation = [...polygon.faces][0].orientation();
  const expCutSegment = prolongSegment(cutSegment, 2 * precision);
  const cutterPoly = rectangleFromSide(expCutSegment, precision);

  if ([...cutterPoly.faces][0].orientation() !== polyMainOrientation) {
    [...cutterPoly.faces][0].reverse();
  }
  // secondary faces will be considered holes
  [...polygon.faces].slice(1).forEach(face => {
    if (face.orientation() === polyMainOrientation) {
      face.reverse();
    }
  });
  // 2 - Perform subtract, result: single poly with as many faces as new polys
  const separatedPoly = subtract(polygon, cutterPoly);

  // 3 - Fix vertices with differences from intersectPoints, due to using poly to cut instead of line
  const intersectPoints = new Set(polygon.intersect(cutSegment));
  const cleanPolygons = [];
  const cleanHoles = [];
  for (const face of separatedPoly.faces) {
    const vertices = new Set();
    for (const edge of face) {
      let vertexToAdd = null;
      intersectPoints.forEach(point => {
        if (edge.shape.ps.distanceTo(point)[0] < precision * 2) {
          vertexToAdd = point;
        } else if (!vertexToAdd) {
          vertexToAdd = edge.shape.ps;
        }
      });
      vertices.add(vertexToAdd);
    }
    const poly = new Polygon();
    poly.addFace([...vertices]);
    if (face.orientation() === polyMainOrientation) {
      cleanPolygons.push(poly);
    } else {
      cleanHoles.push(poly);
    }
  }
  // 4 - Finally, check if any of this polygons overlaps with another, which means it should be a hole
  return createPolygonsFromIntersections(cleanPolygons, cleanHoles);
}

function multiSplitPolygons(polygons, cuts) {
  const splittedPolys = polygons;
  cuts.forEach(cut => {
    let i = 0;
    while (i < splittedPolys.length) {
      const poly = splittedPolys[i];
      // I would prefer to use the .contains method, but it doesn't do proper decimal rounding and leads to detection errors
      if (cut.intersect(poly).length != 0) {
        const newSplittedPolys = splitPolygon(cut, poly);
        if (newSplittedPolys.length > 1) {
          splittedPolys.splice(i, 1);
          splittedPolys.push(...newSplittedPolys);
        }

        break;
      }
      i++;
    }
  });
  return splittedPolys;
}

export { splitPolygon, multiSplitPolygons };
